import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { Folder } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetAccounts } from '../redux/sagas/requests/account';
import { useNavigate } from 'react-router-dom';

const ProjectList = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const AccountSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    '&:last-child': {
        marginBottom: 0,
    },
}));

const AccountHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.surface.inset,
    borderRadius: theme.shape.borderRadius,
}));

const AccountName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    flex: 1,
    cursor: 'pointer',
    '&:hover': {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
    },
}));

const SummaryBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-around',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.surface.inset,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
}));

const SummaryItem = styled(Box)({
    textAlign: 'center',
});

const SummaryValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.size.xl,
    color: theme.palette.primary.main,
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(Folder)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const AccountProjectsWidget = ({ widgetId, config = {}, onRemove }) => {
    const navigate = useNavigate();
    const { data: accounts, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetAccounts(0, 0, null, true),
        cacheKey: 'accounts-pinned',
        transform: (response) => response?.accounts || response || [],
    });

    const totalProjects = (accounts || []).reduce((activeProjects, account) => activeProjects + (account.projects || 0), 0);
    const hasData = (accounts || []).length > 0;

    return (
        <BaseWidget
            title="Pinned Account Projects"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasData ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">
                        No pinned accounts
                    </Typography>
                    <Typography variant="caption">
                        Pin accounts to see them here
                    </Typography>
                </EmptyState>
            ) : (
                <Box>
                    <SummaryBox>
                        <SummaryItem>
                            <SummaryValue>{(accounts || []).length}</SummaryValue>
                            <SummaryLabel>Accounts</SummaryLabel>
                        </SummaryItem>
                        <SummaryItem>
                            <SummaryValue>{totalProjects}</SummaryValue>
                            <SummaryLabel>Projects</SummaryLabel>
                        </SummaryItem>
                    </SummaryBox>

                    <ProjectList>
                        {(accounts || []).map((account) => (
                            <AccountSection key={account._id}>
                                <AccountHeader>
                                    <AccountName
                                        onClick={() => navigate(`/account/${account.slug}`)}
                                    >
                                        {account.name}
                                    </AccountName>
                                    <Chip
                                        label={account.projects || 0}
                                        size="small"
                                        variant="outlined"
                                    />
                                </AccountHeader>
                            </AccountSection>
                        ))}
                    </ProjectList>
                </Box>
            )}
        </BaseWidget>
    );
};

export default AccountProjectsWidget;
